import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import { renderToString } from "react-dom/server.js";
import { BinIcon } from "../../components/Icon/Icon.jsx";
import { LangProviderContext } from "./LangProviderContext.js";
import Cookies from "universal-cookie";
import { createStructuredSelector } from "reselect";
import { appInitAction } from "../../app/appActions.js";
import { connect } from "react-redux";
import moment from "moment";
import { getAvailableCountries } from "../../app/appSelectors.js";
import { userUpdateAction } from "../../modules/Partner/partnerActions.js";
import { AVAILABLE_LOCALES, getLangByLocaleCode, normalizeLocaleCode } from "../../translate/locale.js";

const cookies = new Cookies();

const getStoredLocale = () => cookies.get('locale');

function LangProvider(props) {
    const {
        appInit,
        availableCountries,
        children,
        userUpdate,
    } = props;

    const [locale, setLocaleState] = useState(() => {
        const stored = getStoredLocale();
        if (stored) {
            return normalizeLocaleCode(stored);
        }
        return null;
    });

    const appInitCalled = useRef(false);

    const lang = useMemo(
        () => (locale ? getLangByLocaleCode(locale) : null),
        [locale],
    );

    const _setLocale = useCallback((_locale) => {
        const normalized = normalizeLocaleCode(_locale);

        cookies.set('locale', normalized, { path: '/', maxAge: 60 * 60 * 24 * 365 });

        if (locale && locale !== normalized) {
            userUpdate({ locale: normalized });
        }

        setLocaleState(normalized);
    }, [locale, userUpdate]);

    const _getLangPath = useCallback(
        (path = '', params = {}, options = {}) => {
            const emptyDefault = !!options.emptyDefault;
            let langString = _.get(lang, path, emptyDefault ? '' : path.toString());
            const paramsKeys = Object.keys(params);
            paramsKeys.forEach(paramKey => {
                langString = langString.replaceAll(`{{${paramKey}}}`, params[paramKey])
            })
            langString = langString.replaceAll(/{{icon::([^:}]+)(::([^:}]+))?}}/ig, (...args) => {
                return renderToString(<BinIcon name={args[1]} style={{ fill: args[3] || null }} />)
            });
            return langString;
        },
        [lang],
    );

    useEffect(() => {
        if (!locale) {
            const detected = normalizeLocaleCode(
                (navigator.language || navigator.userLanguage || 'en').slice(0, 2),
            );
            cookies.set('locale', detected, { path: '/', maxAge: 60 * 60 * 24 * 365 });
            setLocaleState(detected);
        }
    }, [locale]);

    useEffect(() => {
        if (locale) {
            document.documentElement.lang = locale;
            moment.locale(locale);
        }
    }, [locale]);

    useEffect(() => {
        if (locale && !appInitCalled.current) {
            appInitCalled.current = true;
            appInit();
        }
    }, [locale, appInit]);

    return <>
        <LangProviderContext.Provider value={{
            availableCountries,
            availableLocales: AVAILABLE_LOCALES,
            getLangPath: _getLangPath,
            locale,
            setLocale: _setLocale,
            setLang: _setLocale,
            lang,
        }}>
            {children}
        </LangProviderContext.Provider>
    </>
}

const mapStateToProps = createStructuredSelector({
    availableCountries: getAvailableCountries,
});

const mapDispatchToProps = (dispatch) => ({
    appInit: (payload) => dispatch(appInitAction(payload)),
    userUpdate: (payload) => dispatch(userUpdateAction(payload))
});

export default connect(mapStateToProps, mapDispatchToProps)(LangProvider);
